<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { endpoints, type GroupOrganizerResponse, type InvitationResponse } from '@/api'
import { ApiError } from '@/api/client-exports'
import { useOrganizerAuthStore } from '@/stores/organizerAuth'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

/**
 * Who organizes a group (SPEC §4.5, §10.5).
 *
 * Owner-only actions are hidden from everyone else, but that is presentation
 * rather than protection — the server refuses them regardless, and hiding a
 * button has never stopped anybody.
 */

const route = useRoute()
const auth = useOrganizerAuthStore()
const groupId = route.params.groupId as string

const organizers = ref<GroupOrganizerResponse[]>([])
const invitations = ref<InvitationResponse[]>([])
const loading = ref(true)
const busy = ref(false)
const error = ref<string | null>(null)
const notice = ref<string | null>(null)
const email = ref('')
const invited = ref<string | null>(null)

const isOwner = computed(() =>
  organizers.value.some((o) => o.organizerUserId === auth.organizerId && o.role === 'Owner'),
)

/*
  Every action on this screen that cannot be undone from this screen (SPEC §9.1).

  One pending action rather than a boolean per button, so adding a fourth is a
  new entry rather than a fourth dialog. Handing over ownership is the one that
  made this necessary: the person who mis-taps it is, a moment later, no longer
  the owner and therefore no longer able to reverse it.
*/
interface PendingAction {
  title: string
  body: string
  confirmLabel: string
  cancelLabel: string
  run: () => Promise<void>
}

const pending = ref<PendingAction | null>(null)

async function confirmPending(): Promise<void> {
  const action = pending.value
  if (!action) return

  await action.run()
  pending.value = null
}

onMounted(load)

async function load(): Promise<void> {
  try {
    organizers.value = await endpoints.listGroupOrganizers(groupId)

    // Only the owner may list invitations, so a refusal here is expected for
    // everyone else rather than a failure worth reporting.
    try {
      invitations.value = await endpoints.listInvitations(groupId)
    } catch {
      invitations.value = []
    }
  } catch (cause) {
    error.value =
      cause instanceof ApiError && cause.status === 404
        ? 'That group is not one of yours.'
        : 'We could not load the organizers.'
  } finally {
    loading.value = false
  }
}

async function run(action: () => Promise<unknown>, success: string): Promise<void> {
  error.value = null
  notice.value = null
  busy.value = true

  try {
    await action()
    await load()
    notice.value = invited.value ? `We have emailed ${invited.value} an invitation.` : success
  } catch (cause) {
    error.value =
      cause instanceof ApiError
        ? (cause.problem?.detail ?? cause.userMessage)
        : 'Something went wrong. Try again.'
  } finally {
    busy.value = false
  }
}

/**
 * Adds somebody, or invites them if they have no account.
 *
 * One field and one button, because an owner does not know or care whether the
 * person they have in mind has signed up before. The distinction is the
 * product's problem, not theirs -- so the direct add is tried first and a
 * "nobody uses that address" answer falls through to an invitation.
 */
async function add(): Promise<void> {
  if (!email.value.trim()) {
    error.value = 'Enter their email address.'
    return
  }

  const address = email.value.trim()

  await run(async () => {
    try {
      await endpoints.addGroupOrganizer(groupId, { email: address })
    } catch (cause) {
      if (cause instanceof ApiError && cause.problemType === 'no-such-organizer') {
        await endpoints.createInvitation(groupId, { email: address })
        invited.value = address
        return
      }

      throw cause
    }

    invited.value = null
  }, `${address} can now help organize.`)

  email.value = ''
}

function revoke(invitation: InvitationResponse): void {
  pending.value = {
    title: `Withdraw the invitation to ${invitation.email}?`,
    body: 'Their link stops working. You can invite them again afterwards.',
    confirmLabel: 'Withdraw it',
    cancelLabel: 'Leave it open',
    run: () =>
      run(
        () => endpoints.revokeInvitation(groupId, invitation.id),
        `The invitation to ${invitation.email} has been withdrawn.`,
      ),
  }
}

function remove(organizer: GroupOrganizerResponse): void {
  pending.value = {
    title: `Remove ${organizer.displayName}?`,
    body: 'They lose access to this group straight away. Nothing they created is deleted, and you can add them back.',
    confirmLabel: `Remove ${organizer.displayName}`,
    cancelLabel: 'Keep them',
    run: () =>
      run(
        () => endpoints.removeGroupOrganizer(groupId, organizer.organizerUserId),
        `${organizer.displayName} is no longer an organizer.`,
      ),
  }
}

function handOver(organizer: GroupOrganizerResponse): void {
  pending.value = {
    title: `Make ${organizer.displayName} the owner?`,
    // Says the part that cannot be taken back, because this is the one action
    // here the person doing it cannot reverse afterwards.
    body: `You stop being the owner and become an ordinary organizer. Only ${organizer.displayName} can give it back.`,
    confirmLabel: 'Hand over the group',
    cancelLabel: 'Stay the owner',
    run: () =>
      run(
        () =>
          endpoints.transferGroupOwnership(groupId, {
            organizerUserId: organizer.organizerUserId,
          }),
        `${organizer.displayName} now owns this group.`,
      ),
  }
}
</script>

<template>
  <v-container class="py-10" style="max-width: 36rem">
    <h1 class="text-h5 mb-6">Organizers</h1>

    <div v-if="loading" class="py-8 text-body-2" style="opacity: 0.75" role="status">Loading…</div>

    <template v-else>
      <div
        v-if="error"
        data-testid="organizers-error"
        role="alert"
        class="mb-4 pa-3 rounded text-body-2"
        style="background-color: rgb(var(--v-theme-error)); color: rgb(var(--v-theme-on-error))"
      >
        {{ error }}
      </div>

      <div
        v-if="notice"
        data-testid="organizers-notice"
        role="status"
        class="mb-4 pa-3 rounded text-body-2"
        style="background-color: rgb(var(--v-theme-surface-variant))"
      >
        {{ notice }}
      </div>

      <v-card v-for="organizer in organizers" :key="organizer.organizerUserId" class="pa-4 mb-3">
        <div class="d-flex align-center justify-space-between flex-wrap ga-2">
          <div>
            <div class="text-body-1">{{ organizer.displayName }}</div>
            <div class="text-body-2" style="opacity: 0.75">{{ organizer.email }}</div>
          </div>

          <v-chip :color="organizer.role === 'Owner' ? 'primary' : undefined" size="small">
            {{ organizer.role }}
          </v-chip>
        </div>

        <div v-if="isOwner && organizer.role !== 'Owner'" class="d-flex flex-wrap ga-2 mt-3">
          <v-btn size="small" variant="text" :disabled="busy" @click="handOver(organizer)">
            Make owner
          </v-btn>
          <v-btn size="small" variant="text" :disabled="busy" @click="remove(organizer)">
            Remove
          </v-btn>
        </div>
      </v-card>

      <template v-if="isOwner">
        <template v-if="invitations.some((i) => i.status === 'Pending')">
          <h2 class="text-subtitle-1 mt-8 mb-2">Invited, not yet joined</h2>

          <v-card
            v-for="invitation in invitations.filter((i) => i.status === 'Pending')"
            :key="invitation.id"
            class="pa-4 mb-2"
            data-testid="pending-invitation"
          >
            <div class="d-flex align-center justify-space-between flex-wrap ga-2">
              <div class="text-body-2">{{ invitation.email }}</div>
              <v-btn size="small" variant="text" :disabled="busy" @click="revoke(invitation)">
                Withdraw
              </v-btn>
            </div>
          </v-card>
        </template>

        <h2 class="text-subtitle-1 mt-8 mb-2">Add an organizer</h2>
        <p class="text-body-2 mb-3" style="opacity: 0.75">
          If they already have an account they will be added straight away. If not, we will email
          them an invitation.
        </p>

        <v-form novalidate @submit.prevent="add">
          <v-text-field
            v-model="email"
            label="Email"
            name="email"
            type="email"
            autocomplete="off"
            :disabled="busy"
            class="mb-2"
          />
          <v-btn type="submit" color="primary" :loading="busy" :disabled="busy">Add</v-btn>
        </v-form>
      </template>
    </template>
    <ConfirmDialog
      :model-value="pending !== null"
      :title="pending?.title ?? ''"
      :body="pending?.body"
      :confirm-label="pending?.confirmLabel ?? 'Confirm'"
      :cancel-label="pending?.cancelLabel ?? 'Cancel'"
      :busy="busy"
      destructive
      @update:model-value="pending = null"
      @confirm="confirmPending"
    />
  </v-container>
</template>
